import {
  useEffect,
  useRef,
  useState,
} from 'react';

import type {
  BackupExport,
} from '../application/backup-export.ts';

import type {
  PreparedRestore,
  RestoreManager,
} from '../application/restore.ts';

import type {
  RestoreFailureCode,
  RestorePreviewToken,
  RestoreRecoveryOutcome,
  RestoreResult,
} from '../domain/restore.ts';

import {
  Button,
  Card,
  Confirmation,
  ErrorMessage,
} from './primitives.tsx';

function restoreError(
  code: RestoreFailureCode,
) {
  switch (code) {
    case 'RESTORE_INVALID_BACKUP':
      return (
        'El archivo no es un backup FÉNIX válido. '
        + 'No se ha sustituido ningún dato activo.'
      );

    case 'RESTORE_SCHEMA_FUTURE':
      return (
        'Este backup requiere una versión de FÉNIX '
        + 'más reciente.'
      );

    case 'RESTORE_PREVIEW_STALE':
      return (
        'Los datos cambiaron desde la vista previa. '
        + 'Selecciona el backup de nuevo antes de restaurar.'
      );

    case 'RESTORE_IN_PROGRESS':
      return (
        'Ya existe un Restore protegido en curso. '
        + 'No se iniciará otro.'
      );

    case 'RESTORE_OPERATION_CONFLICT':
      return (
        'La operación de Restore ya no coincide con '
        + 'la vista previa preparada.'
      );

    case 'RESTORE_CORRUPTION':
      return (
        'FÉNIX no pudo verificar de forma segura el Restore.'
      );

    case 'STORAGE_QUOTA':
      return (
        'No hay espacio local suficiente para preparar '
        + 'esta restauración.'
      );

    case 'TRANSACTION_ABORTED':
      return (
        'El almacenamiento canceló la operación. '
        + 'No se confirmará el Restore sin verificarlo.'
      );

    case 'STORAGE_UNAVAILABLE':
      return (
        'El almacenamiento local no está disponible '
        + 'en este momento.'
      );
  }
}

function startupNotice(
  recovery:
    RestoreResult<RestoreRecoveryOutcome>,
) {
  if (!recovery.ok) {
    return {
      kind: 'error' as const,
      text:
        'FÉNIX no pudo resolver automáticamente '
        + 'el estado previo de Restore ('
        + recovery.code
        + ').',
    };
  }

  switch (recovery.value.status) {
    case 'RECOVERED_COMPLETED':
      return {
        kind: 'status' as const,
        text:
          'FÉNIX completó de forma segura un Restore '
          + 'interrumpido anteriormente.',
      };

    case 'RECOVERED_ROLLED_BACK':
      return {
        kind: 'status' as const,
        text:
          'FÉNIX revirtió de forma segura un Restore '
          + 'interrumpido anteriormente.',
      };

    case 'PREVIEW_PENDING':
      return {
        kind: 'status' as const,
        text:
          'Existe una vista previa de Restore preparada '
          + 'en otra pestaña o sesión. No se iniciará '
          + 'otra mientras siga pendiente.',
      };

    case 'IDLE':
      return null;
  }
}

export function Settings({
  backup,
  restore,
  restoreRecovery,
  pending,
}: {
  backup:
    BackupExport | null;

  restore:
    RestoreManager | null;

  restoreRecovery:
    RestoreResult<RestoreRecoveryOutcome>;

  pending(
    value: boolean,
  ): void;
}) {
  const fileInput =
    useRef<HTMLInputElement>(null);

  const [backupBusy, setBackupBusy] =
    useState(false);

  const [backupStatus, setBackupStatus] =
    useState('');

  const [backupError, setBackupError] =
    useState('');

  const [restoreBusy, setRestoreBusy] =
    useState(false);

  const [restoreStatus, setRestoreStatus] =
    useState('');

  const [restoreFailure, setRestoreFailure] =
    useState('');

  const [prepared, setPrepared] =
    useState<PreparedRestore | null>(
      null,
    );

  const [confirmRestore, setConfirmRestore] =
    useState(false);

  const startupPreviewPending =
    restoreRecovery.ok
    && restoreRecovery.value.status
      === 'PREVIEW_PENDING';

  const [
    orphanPreview,
    setOrphanPreview,
  ] =
    useState<
      RestorePreviewToken | null
    >(null);

  const [
    orphanResolved,
    setOrphanResolved,
  ] =
    useState(
      !startupPreviewPending,
    );

  const [
    orphanLoading,
    setOrphanLoading,
  ] =
    useState(
      startupPreviewPending,
    );

  const recovery =
    startupNotice(
      restoreRecovery,
    );

  const showRecovery =
    !(
      startupPreviewPending
      && orphanResolved
    );

  useEffect(
    () => {
      if (
        !startupPreviewPending
        || orphanResolved
      ) {
        setOrphanLoading(false);
        return;
      }

      pending(true);

      if (!restore) {
        setOrphanLoading(false);

        setRestoreFailure(
          'No se puede resolver la vista previa '
          + 'pendiente porque Restore no esta disponible.',
        );

        return;
      }

      let active = true;

      void restore
        .pendingPreview()
        .then(
          result => {
            if (!active) {
              return;
            }

            setOrphanLoading(false);

            if (!result.ok) {
              setRestoreFailure(
                restoreError(
                  result.code,
                ),
              );

              return;
            }

            if (result.value === null) {
              setOrphanResolved(true);
              pending(false);
              reloadSoon();
              return;
            }

            setOrphanPreview(
              result.value,
            );
          },
        )
        .catch(
          () => {
            if (!active) {
              return;
            }

            setOrphanLoading(false);

            setRestoreFailure(
              'No se pudo leer de forma confirmada '
              + 'la vista previa pendiente.',
            );
          },
        );

      return () => {
        active = false;
      };
    },
    [
      orphanResolved,
      pending,
      restore,
      startupPreviewPending,
    ],
  );

  function reloadSoon() {
    window.setTimeout(
      () => {
        location.reload();
      },
      150,
    );
  }

  async function exportBackup() {
    if (!backup || backupBusy) {
      return;
    }

    setBackupBusy(true);
    setBackupStatus('');
    setBackupError('');

    try {
      const result =
        await backup.createFile();

      if (!result.ok) {
        setBackupError(
          'No se pudo crear el backup ('
          + result.code
          + ').',
        );

        return;
      }

      const blob =
        new Blob(
          [result.value.json],
          {
            type:
              'application/json',
          },
        );

      const url =
        URL.createObjectURL(blob);

      const anchor =
        document.createElement('a');

      anchor.href = url;
      anchor.download =
        result.value.filename;
      anchor.hidden = true;

      document.body.append(anchor);
      anchor.click();
      anchor.remove();

      requestAnimationFrame(
        () =>
          URL.revokeObjectURL(url),
      );

      setBackupStatus(
        'Backup preparado para guardar.',
      );
    }
    catch {
      setBackupError(
        'No se pudo preparar el archivo de backup.',
      );
    }
    finally {
      setBackupBusy(false);
    }
  }

  async function prepareFile(
    file: File,
  ) {
    if (
      !restore
      || restoreBusy
      || prepared
      || (
        startupPreviewPending
        && !orphanResolved
      )
    ) {
      return;
    }

    setRestoreBusy(true);
    setRestoreStatus('');
    setRestoreFailure('');

    try {
      if (
        !file.name
          .toLowerCase()
          .endsWith('.json')
      ) {
        setRestoreFailure(
          'Selecciona un archivo JSON de backup FÉNIX.',
        );

        return;
      }

      const json =
        await file.text();

      const result =
        await restore.prepareJson(
          json,
        );

      if (!result.ok) {
        setRestoreFailure(
          restoreError(
            result.code,
          ),
        );

        return;
      }

      setPrepared(
        result.value,
      );

      pending(true);

      setRestoreStatus(
        'Backup validado. Revisa la vista previa '
        + 'antes de confirmar el reemplazo.',
      );
    }
    catch {
      setRestoreFailure(
        'No se pudo leer el archivo seleccionado.',
      );
    }
    finally {
      if (fileInput.current) {
        fileInput.current.value = '';
      }

      setRestoreBusy(false);
    }
  }

  async function cancelPrepared() {
    if (
      !restore
      || !prepared
      || restoreBusy
    ) {
      return;
    }

    const current =
      prepared;

    setRestoreBusy(true);
    setRestoreStatus('');
    setRestoreFailure('');

    try {
      const result =
        await restore.cancel(
          current,
        );

      if (!result.ok) {
        setRestoreFailure(
          restoreError(
            result.code,
          ),
        );

        return;
      }

      setPrepared(null);
      pending(false);

      setRestoreStatus(
        'Restore cancelado. '
        + 'Los datos activos no se han sustituido.',
      );
    }
    catch {
      setRestoreFailure(
        'No se pudo cancelar de forma confirmada '
        + 'la vista previa de Restore.',
      );
    }
    finally {
      setRestoreBusy(false);
    }
  }

  async function applyPrepared() {
    if (
      !restore
      || !prepared
      || restoreBusy
    ) {
      return;
    }

    const current =
      prepared;

    setConfirmRestore(false);
    setRestoreBusy(true);
    setRestoreStatus('');
    setRestoreFailure('');

    try {
      const result =
        await restore.confirm(
          current,
        );

      if (result.ok) {
        setPrepared(null);
        pending(false);

        setRestoreStatus(
          'Restore completado y verificado. '
          + 'Recargando FÉNIX…',
        );

        reloadSoon();
        return;
      }

      if (
        result.code
          === 'RESTORE_PREVIEW_STALE'
      ) {
        setPrepared(null);
        pending(false);

        setRestoreFailure(
          restoreError(
            result.code,
          ),
        );

        return;
      }

      /*
       * A protected failure can have changed controlEpoch
       * even when the original dataset is recovered.
       * Ask the Restore engine to settle any persisted fence
       * before deciding whether this App composition is safe
       * to keep using.
       */
      const recoveryResult =
        await restore.recover();

      if (!recoveryResult.ok) {
        setRestoreFailure(
          restoreError(
            recoveryResult.code,
          )
          + ' FÉNIX mantiene la protección; '
          + 'no continúes editando datos en esta pestaña.',
        );

        return;
      }

      if (
        recoveryResult.value.status
          === 'PREVIEW_PENDING'
      ) {
        setRestoreFailure(
          restoreError(
            result.code,
          )
          + ' La vista previa sigue preparada; '
          + 'puedes reintentar o cancelarla.',
        );

        return;
      }

      setPrepared(null);
      pending(false);

      if (
        recoveryResult.value.status
          === 'RECOVERED_COMPLETED'
      ) {
        setRestoreStatus(
          'El Restore interrumpido se completó '
          + 'durante la recuperación. Recargando FÉNIX…',
        );
      }
      else if (
        recoveryResult.value.status
          === 'RECOVERED_ROLLED_BACK'
      ) {
        setRestoreStatus(
          'El Restore fue revertido de forma segura. '
          + 'Recargando FÉNIX…',
        );
      }
      else {
        setRestoreFailure(
          restoreError(
            result.code,
          )
          + ' El estado protegido ya está cerrado. '
          + 'FÉNIX se recargará para refrescar '
          + 'dataset y epoch.',
        );
      }

      reloadSoon();
    }
    catch {
      setRestoreFailure(
        'Se perdió la confirmación de la operación. '
        + 'FÉNIX no asumirá que el Restore terminó.',
      );
    }
    finally {
      setRestoreBusy(false);
    }
  }

  async function discardOrphanPreview() {
    if (
      !restore
      || !orphanPreview
      || restoreBusy
    ) {
      return;
    }

    const current =
      orphanPreview;

    setRestoreBusy(true);
    setRestoreStatus('');
    setRestoreFailure('');

    try {
      const result =
        await restore
          .discardPending(
            current,
          );

      if (!result.ok) {
        /*
         * Another tab may have resolved the pending
         * operation between discovery and this click.
         * Re-read the protocol state before deciding
         * whether this composition is still safe.
         */
        const checked =
          await restore.recover();

        if (
          checked.ok
          && checked.value.status
            === 'IDLE'
        ) {
          setOrphanPreview(null);
          setOrphanResolved(true);
          pending(false);

          setRestoreStatus(
            'La operacion pendiente ya fue resuelta '
            + 'en otra pestaña. Recargando FENIX...',
          );

          reloadSoon();
          return;
        }

        setRestoreFailure(
          restoreError(
            result.code,
          ),
        );

        return;
      }

      setOrphanPreview(null);
      setOrphanResolved(true);
      pending(false);

      setRestoreStatus(
        'Preparacion de Restore descartada. '
        + 'Los datos activos no se han sustituido.',
      );
    }
    catch {
      setRestoreFailure(
        'No se pudo descartar de forma confirmada '
        + 'la preparacion pendiente.',
      );
    }
    finally {
      setRestoreBusy(false);
    }
  }

  const canSelectRestore =
    restore !== null
    && restoreRecovery.ok
    && orphanResolved
    && prepared === null
    && !restoreBusy;

  return (
    <div className="root-page">
      <h1>Settings</h1>

      {
        recovery
        && showRecovery
          ? (
            recovery.kind === 'error'
              ? (
                <div
                  data-testid="restore-startup-error"
                >
                  <ErrorMessage>
                    {recovery.text}
                  </ErrorMessage>
                </div>
              )
              : (
                <p
                  role="status"
                  className="secondary"
                  data-testid="restore-startup-status"
                >
                  {recovery.text}
                </p>
              )
          )
          : null
      }

      <Card>
        <h2>Backup</h2>

        <p>
          Exporta una copia lógica de tus datos
          para guardarla o moverla mediante
          las opciones del sistema.
        </p>

        <p
          className="secondary"
          data-testid="backup-warning"
        >
          Este archivo contiene datos personales.
          FÉNIX NO cifra este archivo.
          Guárdalo en un lugar seguro.
        </p>

        <Button
          busy={backupBusy}
          disabled={!backup}
          data-testid="export-backup"
          onClick={
            () => {
              void exportBackup();
            }
          }
        >
          Exportar backup JSON
        </Button>

        {
          backupStatus
            ? (
              <p
                role="status"
                data-testid="backup-status"
              >
                {backupStatus}
              </p>
            )
            : null
        }

        {
          backupError
            ? (
              <div data-testid="backup-error">
                <ErrorMessage>
                  {backupError}
                </ErrorMessage>
              </div>
            )
            : null
        }
      </Card>

      <Card>
        <h2>Restore</h2>

        <p>
          Restaura un backup lógico FÉNIX
          mediante reemplazo completo del
          dataset local activo.
        </p>

        <p
          className="secondary"
          data-testid="restore-warning"
        >
          Confirmar un Restore reemplazará
          los datos locales actuales.
          La aplicación valida y prepara
          primero una generación separada;
          no repara ni mezcla silenciosamente
          el archivo con los datos actuales.
        </p>

        {
          startupPreviewPending
          && !orphanResolved
            ? (
              <div
                className="field-stack"
                data-testid="restore-orphan-preview"
              >
                <h3>
                  Restore pendiente
                </h3>

                <p>
                  Existe una generacion de Restore
                  validada cuya pestaña original ya
                  no conserva la vista previa local.
                </p>

                <p className="secondary">
                  FENIX no la confirmara
                  automaticamente. Para continuar,
                  descarta esta preparacion y vuelve
                  a seleccionar el archivo si aun
                  quieres restaurarlo.
                </p>

                {
                  orphanLoading
                    ? (
                      <p
                        role="status"
                        data-testid="restore-orphan-loading"
                      >
                        Verificando preparacion pendiente...
                      </p>
                    )
                    : orphanPreview
                      ? (
                        <Button
                          busy={restoreBusy}
                          data-testid="restore-orphan-discard"
                          onClick={
                            () => {
                              void discardOrphanPreview();
                            }
                          }
                        >
                          Descartar preparacion pendiente
                        </Button>
                      )
                      : null
                }
              </div>
            )
            : null
        }

        <input
          ref={fileInput}
          hidden
          type="file"
          accept=".json,application/json"
          data-testid="restore-file"
          onChange={
            event => {
              const file =
                event.currentTarget
                  .files?.[0];

              if (file) {
                void prepareFile(
                  file,
                );
              }
            }
          }
        />

        <Button
          busy={restoreBusy}
          disabled={!canSelectRestore}
          data-testid="restore-select"
          onClick={
            () => {
              fileInput.current?.click();
            }
          }
        >
          Seleccionar backup JSON
        </Button>

        {
          prepared
            ? (
              <div
                className="field-stack"
                data-testid="restore-preview"
              >
                <h3>Vista previa validada</h3>

                <p>
                  Backup:
                  {' '}
                  {prepared.summary.backupId}
                </p>

                <p>
                  Creado por:
                  {' '}
                  {
                    prepared.summary
                      .producerAppVersion
                  }
                </p>

                <p>
                  Exportado:
                  {' '}
                  {prepared.summary.exportedAt}
                </p>

                <p>
                  Training:
                  {' '}
                  {
                    prepared.summary
                      .counts.training
                  }
                  {' · '}
                  Nutrition:
                  {' '}
                  {
                    prepared.summary
                      .counts.nutrition
                  }
                </p>

                <p>
                  Progress:
                  {' '}
                  {
                    prepared.summary
                      .counts.progress
                  }
                  {' · '}
                  Rutina diaria:
                  {' '}
                  {
                    prepared.summary
                      .counts.dailyRoutine
                  }
                </p>

                <p>
                  Revisiones históricas:
                  {' '}
                  {
                    prepared.summary
                      .counts.history
                  }
                </p>

                <p className="secondary">
                  Esta vista previa está ligada
                  al dataset, revisión y epoch
                  actuales. Si los datos cambian
                  antes de confirmar, el Restore
                  será rechazado como obsoleto.
                </p>

                <div className="action-group">
                  <Button
                    busy={restoreBusy}
                    data-testid="restore-confirm-open"
                    onClick={
                      () => {
                        setConfirmRestore(
                          true,
                        );
                      }
                    }
                  >
                    Confirmar Restore
                  </Button>

                  <Button
                    busy={restoreBusy}
                    data-testid="restore-cancel"
                    onClick={
                      () => {
                        void cancelPrepared();
                      }
                    }
                  >
                    Cancelar Restore
                  </Button>
                </div>
              </div>
            )
            : null
        }

        {
          restoreStatus
            ? (
              <p
                role="status"
                data-testid="restore-status"
              >
                {restoreStatus}
              </p>
            )
            : null
        }

        {
          restoreFailure
            ? (
              <div
                data-testid="restore-error"
              >
                <ErrorMessage>
                  {restoreFailure}
                </ErrorMessage>
              </div>
            )
            : null
        }
      </Card>

      <a
        className="settings-link"
        href="#Home"
      >
        Volver a Home
      </a>

      {
        confirmRestore
        && prepared
          ? (
            <Confirmation
              title="Restaurar backup"
              message={
                'Esta operación reemplazará '
                + 'completamente los datos locales '
                + 'actuales por la generación validada. '
                + 'No se mezclarán ambos datasets.'
              }
              confirmLabel={
                'Reemplazar datos y restaurar'
              }
              onAnswer={
                answer => {
                  setConfirmRestore(
                    false,
                  );

                  if (answer) {
                    void applyPrepared();
                  }
                }
              }
            />
          )
          : null
      }
    </div>
  );
}